import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  GeneralRecommendationResponse,
  SourcingPurchaseBulkCreateRequest,
} from 'api-clients/monolith';
import { SourcingService } from 'api-clients/monolith/services/SourcingService';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToaster } from '@/components/base/toaster';
import { Typography } from '@/components/ui/typography';
import { useRecommendationContext } from '@/contexts/recommendation-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { addDays, formatShortDate } from '@/utils/date-util';

import { useQueryInvalidation } from '../use-query-invalidation';
import { useGeneralRecommendationSchema } from '../validation/general-recommendation-schema';
import { ChannelSummary } from './channel-summary';

import type {
  GeneralRecommendationFormFields,
  HiringGoalDetails,
  OpeningDetails,
  ValidatedGeneralRecommendationFields,
} from '../types';

interface GeneralRecommendationFormProps {
  opening: OpeningDetails;
  hiringGoal?: HiringGoalDetails;
  recommendedChannels: GeneralRecommendationResponse['recommended_channels'];
}

export function GeneralRecommendationForm({
  opening,
  hiringGoal,
  recommendedChannels,
}: GeneralRecommendationFormProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { notifySuccess, notifyError } = useToaster();
  const {
    setIsSubmitting,
    closeRecommendationPostingDialog,
    selectedChannels,
    setSelectedChannels,
  } = useRecommendationContext();
  const { invalidateRecommendationPostingQueries } = useQueryInvalidation();

  const channelSlugs = useMemo(
    () => recommendedChannels.map(c => c.slug),
    [recommendedChannels],
  );

  const validationSchema = useGeneralRecommendationSchema(
    channelSlugs,
    selectedChannels,
  );

  const hookForm = useForm<
    GeneralRecommendationFormFields,
    unknown,
    ValidatedGeneralRecommendationFields
  >({
    resolver: zodResolver(validationSchema),
    defaultValues: recommendedChannels.reduce((acc, channel) => {
      return {
        ...acc,
        [channel.slug]: {
          budget: channel.budget,
          duration: channel.duration,
        },
      };
    }, {}),
  });
  const { handleSubmit, watch, clearErrors } = hookForm;

  /* Post job by calling the bulk create API */
  const { mutate: postJob } = useMutation({
    mutationFn: (requestBody: SourcingPurchaseBulkCreateRequest) =>
      SourcingService.postInternalApiSourcingSourcingPurchasesBulkCreate(
        requestBody,
      ),
    onSuccess: async data => {
      const { total, created, failed } = data.summary;
      if (created === total && failed === 0) {
        notifySuccess({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{total, plural, one {Job posted successfully} other {Jobs posted successfully}}',
              description: 'Success message for successful job posting',
            },
            { total },
          ),
        });
      } else {
        notifyError({
          message: intl.formatMessage(
            {
              defaultMessage:
                '{total, plural, one {Failed to create job posting} other {Among {total} jobs, {created} jobs were posted successfully and {failed} jobs failed}}',
              description: 'Error message for job posting creation',
            },
            { total, created, failed },
          ),
        });
      }

      setIsSubmitting(false);
      await invalidateRecommendationPostingQueries();

      closeRecommendationPostingDialog();
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to create job posting',
          description: 'Error message for job posting creation',
        }),
      });
      setIsSubmitting(false);
    },
  });

  const handlePostJob = handleSubmit(formValues => {
    const formData: SourcingPurchaseBulkCreateRequest = {
      sourcing_purchases: recommendedChannels
        .filter(channel => selectedChannels.includes(channel.slug))
        .map(channel => {
          const { budget, duration } = formValues[channel.slug];

          return {
            funnel_external_id: opening.externalId,
            posting_title: opening.title,
            sourcing_channel_slug: channel.slug,
            budget,
            budget_type: 'lifetime',
            total_budget: budget,
            starts_at: new Date().toISOString(),
            end_at: addDays(new Date(), duration).toISOString(),
            pool_attributes: null,
          };
        }),
    };

    setIsSubmitting(true);
    postJob(formData);
  });

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void handlePostJob();
  };

  const handleChannelSelectionChange = (channelSlug: string) => {
    setSelectedChannels(prev => {
      if (prev.includes(channelSlug)) {
        clearErrors(channelSlug);
        return prev.filter(slug => slug !== channelSlug);
      }
      return [...prev, channelSlug];
    });
  };

  const formValues = watch();
  const totalBudget = useMemo(() => {
    return Object.entries(formValues)
      .filter(([channelSlug]) => selectedChannels.includes(channelSlug))
      .map(([_, channel]) => channel)
      .reduce((acc, channel) => {
        return acc + (channel.budget ?? 0);
      }, 0);
  }, [formValues, selectedChannels]);

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Typography variant="body2" className="!text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Post to recommended channels"
            description="Dialog subtitle for multi-board posting from an algorithmic recommendation"
          />
        </Typography>
        {hiringGoal && (
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Hiring goal end date : {hireByDate} ({daysRemaining} days remaining)"
              description="Shows goal deadline and days left above the channel checklist"
              values={{
                hireByDate: formatShortDate(hiringGoal.hireBy),
                daysRemaining: hiringGoal.duration - hiringGoal.daysElapsed,
              }}
            />
          </Typography>
        )}
      </div>

      <form id="recommendation-form" onSubmit={handleFormSubmit}>
        <div className="border-fx-neutral-200 flex flex-col rounded-md border">
          {recommendedChannels.map((channel, index) => (
            <ChannelSummary
              key={channel.slug}
              channelDetails={channel}
              selectedChannels={selectedChannels}
              handleChannelSelectionChange={handleChannelSelectionChange}
              hookForm={hookForm}
              isLast={index === recommendedChannels.length - 1}
            />
          ))}
        </div>
      </form>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {/* Total budget */}
      <div className="flex justify-between">
        <div className="flex flex-col gap-1.5">
          <Typography variant="body2" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Estimated total spend"
              description="Label for summed budget across all selected boards in this dialog"
            />
          </Typography>
          <Typography variant="small1" className="!text-fx-neutral-600">
            <FormattedMessage
              defaultMessage="Based on per-channel budget × duration"
              description="Footnote explaining how the total above is calculated"
            />
          </Typography>
        </div>

        <Typography variant="large2">{formatCurrency(totalBudget)}</Typography>
      </div>
    </>
  );
}
